import { useAccount, useMsal } from "@azure/msal-react"
import { useState } from "react"

import {
  AdvancedForm,
  AdvancedFormData,
  AdvancedFormFieldProps,
  AdvancedForms,
} from "src/components/common/advancedForm"
import { Button } from "src/components/common/button"
import { Drawer } from "src/components/common/drawer"
import { BasicLayoutStyleProps } from "src/components/layouts/basic"
import { create } from "src/helpers/bem"
import { postAccount } from "src/queriesXRM/account"
import { useAccount as useUser } from "src/states/account"
import { mergeAccountForm, useAccountForm } from "src/states/accountForm"

import { Dialogs, mergeDialog, useDialog } from "src/states/dialogs"

import { addNotification } from "src/states/notifications"
import { TranslationMessages } from "src/translations"

import { xRMApiAccount } from "src/types/xRM"

import styles from "./CompleteProfileDialog.module.scss"

const bem = create(styles, "CompleteProfileDialog")

type CompleteProfileDialogProps = {
  messages: TranslationMessages
  style: BasicLayoutStyleProps
}

export const CompleteProfileDialog = ({
  messages,
  style,
}: CompleteProfileDialogProps) => {
  const account = useUser()
  const form = useAccountForm()
  const { accounts } = useMsal()
  const msalAccount = useAccount(accounts[0])
  const { open } = useDialog(
    "completeProfileDialog",
  ) as Dialogs["completeProfileDialog"]
  const [loading, setLoading] = useState<boolean>(false)

  const profileMessages = messages.pages.account.overview.profile
  const companyFormIndex = form.findIndex((f) => f.id === "company")
  const personalsFormIndex = form.findIndex((f) => f.id === "personals")

  const getFilteredFormData = (filter: "personals" | "company") => {
    return form
      .filter((f) => f.id === filter)
      .map((f) => {
        let filteredFields = f.fields

        if (filter === "company") {
          filteredFields = filteredFields.filter(
            (field) =>
              field.name !== "companyEmail" &&
              field.name !== "companyWebsite" &&
              field.name !== "companyType",
          )
        }

        const fields = filteredFields.map((field) => {
          let newValue = field.value
          let isDisabled = false

          if (field.name === "firstName") {
            isDisabled = true
            newValue =
              (msalAccount?.idTokenClaims?.given_name as string | undefined) ??
              ""
          }
          if (field.name === "lastName") {
            isDisabled = true
            newValue =
              (msalAccount?.idTokenClaims?.family_name as string | undefined) ??
              ""
          }
          if (field.name === "email") {
            isDisabled = true
            newValue =
              (msalAccount?.idTokenClaims?.email as string | undefined) ?? ""
          }

          return {
            ...field,
            sizeVariant: "medium",
            containerClassName: bem("form__field"),
            value: newValue,
            disabled: isDisabled,
          }
        })

        return { ...f, fields }
      })
  }

  const personalsFormData: AdvancedFormData = [
    ...getFilteredFormData("personals"),
  ] as AdvancedForms[]
  const companyFormData: AdvancedFormData = [
    ...getFilteredFormData("company"),
  ] as AdvancedForms[]

  const mergeFormToAccount = async () => {
    const personalsFields = personalsFormData[0]?.fields ?? []
    const companyFields = companyFormData[0]?.fields ?? []

    const personalsFieldMap = Object.fromEntries(
      personalsFields.map((field) => [field.name, field.value]),
    )
    const companyFieldMap = Object.fromEntries(
      companyFields.map((field) => [field.name, field.value]),
    )

    const update = {
      ...account,
      firstName: personalsFieldMap["firstName"]?.toString(),
      lastName: personalsFieldMap["lastName"]?.toString(),
      email: msalAccount?.idTokenClaims?.email as string | undefined,
      phone: personalsFieldMap["phone"]?.toString(),
      language: Number(personalsFieldMap["language"]),
      salutation: personalsFieldMap["salutation"]?.toString(),
      company1: companyFieldMap["company1"]?.toString(),
      companyStreet: companyFieldMap["companyStreet"]?.toString(),
      companyZipCode: companyFieldMap["companyZipCode"]?.toString(),
      companyCity: companyFieldMap["companyCity"]?.toString(),
      companyCountryCodeId: companyFieldMap["companyCountryCodeId"]?.toString(),
      companyWebsite: companyFieldMap["companyWebsite"]?.toString(),
      companyEmail: companyFieldMap["companyEmail"]?.toString(),
      companyType: companyFieldMap["companyType"]?.toString(),
    }

    await postAccount(update as xRMApiAccount)
    mergeDialog("completeProfileDialog", { unsaved: false })
  }

  const handleSave = async () => {
    try {
      setLoading(true)
      await mergeFormToAccount()
      mergeDialog("completeProfileDialog", { open: false })
      addNotification({
        autoclose: true,
        variant: "success",
        content: profileMessages.complete.notifications.success,
      })
    } catch (error) {
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  const handleMergePersonals = (
    _index: number,
    items: AdvancedFormFieldProps,
    name: string | null,
  ) => {
    mergeAccountForm(personalsFormIndex, items, name)
  }

  const handleMergeCompany = (
    _index: number,
    items: AdvancedFormFieldProps,
    name: string | null,
  ) => {
    mergeAccountForm(companyFormIndex, items, name)
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      style={style}
      title={profileMessages.complete.title}
      onClose={() => {}}
      onOpen={() => {}}
    >
      <div className={bem()}>
        <div className={bem("header")}>
          <h2 className={bem("header__title")}>
            {profileMessages.complete.title}
          </h2>
          <span className={bem("header__subtitle")}>
            {profileMessages.complete.subtitle}
          </span>
        </div>
        <div className={bem("fields")}>
          <AdvancedForm
            data={personalsFormData}
            fieldsContainerClassName={bem("form")}
            formMessages={profileMessages.complete}
            handleMergeField={handleMergePersonals}
            messages={messages}
          />
        </div>

        <div className={bem("fields")}>
          <AdvancedForm
            data={companyFormData}
            fieldsContainerClassName={bem("form")}
            formMessages={profileMessages.complete}
            handleMergeField={handleMergeCompany}
            messages={messages}
          />
        </div>
        <div className={bem("footer")}>
          <Button
            buttonType="primary"
            loading={loading}
            title={profileMessages.complete.saveButton}
            onClick={handleSave}
          />
        </div>
      </div>
    </Drawer>
  )
}
